import { useRef } from "react";
import {
  TCustomCheckoutComponentItem,
  TCustomCheckoutComponentType,
  isContainerItem,
} from "../types/CustomCheckout";
import { TDropZoneTarget } from "../context/CheckoutBuilderContext";
import CheckoutFunctions from "../functions/CheckoutFunctions";
import NumberFunctions from "../functions/NumberFunctions";
import theme from "../../layouts/Theme";
import { newCustomCheckoutText } from "../constants";

/**
 * Hook que encapsula toda a lógica de Drag-and-Drop do checkout builder.
 *
 * Extrai do CustomCheckoutPage:
 * - Refs de DnD (dragOverItem, dragOverItemOld, dragItemComponent)
 * - Debounce de drop (dropItemDate)
 * - Handlers de paleta (dragStartComponent, dropComponent)
 * - Handler de reordenação (dropItem)
 * - Funções auxiliares (createNewItem, insertIntoContainer)
 */
export function useCheckoutDnD(
  list: TCustomCheckoutComponentItem[],
  setList: (value: TCustomCheckoutComponentItem[]) => void,
  setEditItem: (item: TCustomCheckoutComponentItem | undefined) => void,
  components: { type: TCustomCheckoutComponentType }[],
) {
  // ─── Refs compartilhados ─────────────────────────────────────────
  const dragOverItem = useRef("");
  const dragOverItemOld = useRef("");
  const dropZoneTarget = useRef<TDropZoneTarget>("");
  const dragItemComponent = useRef(-1);
  const dropItemDate = useRef(new Date());

  // ─── Funções auxiliares ──────────────────────────────────────────

  /** Cria um novo item baseado no tipo do componente da paleta */
  const createNewItem = (
    componentType: TCustomCheckoutComponentType,
  ): TCustomCheckoutComponentItem | undefined => {
    const newId = NumberFunctions.getRandomInt(999999).toString();
    switch (componentType) {
      case TCustomCheckoutComponentType.text:
        return { ...newCustomCheckoutText, id: newId };
      case TCustomCheckoutComponentType.image:
        return {
          id: newId,
          name: "Imagem",
          order: 0,
          type: componentType,
          sx: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
          title: "",
          subtitle: "",
          fixedTop: false,
          children: [],
        };
      case TCustomCheckoutComponentType.stopWatch:
        return {
          id: newId,
          name: "Cronômetro",
          order: 0,
          type: componentType,
          sx: {
            backgroundColor: "#F44747",
            color: "white",
          },
          title: "Oferta por tempo limitado",
          subtitle: "O tempo acabou!",
          fixedTop: false,
          hours: 0,
          minutes: 15,
          seconds: 0,
          milleseconds: 900000,
          children: [],
        };
      default:
        return undefined;
    }
  };

  /** Insere item como filho de um container (list) */
  const insertIntoContainer = (
    container: TCustomCheckoutComponentItem,
    newItem: TCustomCheckoutComponentItem,
    cleanList: TCustomCheckoutComponentItem[],
  ) => {
    const updatedContainer = {
      ...container,
      children: [...container.children, newItem],
    };
    let order = 0;
    updatedContainer.children = updatedContainer.children
      .filter(
        (c) =>
          c.type !== TCustomCheckoutComponentType.empty &&
          c.type !== TCustomCheckoutComponentType.none,
      )
      .map((c) => {
        order += 1;
        return { ...c, order };
      });
    const newList = CheckoutFunctions.updateList(cleanList, updatedContainer);
    setList([...newList]);
  };

  // ─── Handlers de paleta ──────────────────────────────────────────

  /** Inicia o drag de um componente da paleta */
  const dragStartComponent = (e: React.DragEvent<HTMLLIElement>, position: number) => {
    dragItemComponent.current = position;
  };

  /** Enter na paleta (atualmente sem ação) */
  const dragEnterComponent = (_e: React.DragEvent<HTMLLIElement>, _position: number) => {
    // Reservado para uso futuro
  };

  /** Reordena a lista e recalcula order */
  const reorderList = (items: TCustomCheckoutComponentItem[]) => {
    let order = 0;
    items.forEach((ci) => {
      order += 1;
      ci.order = order;
    });
    return items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  /** Drop de um componente da paleta no canvas */
  const dropComponent = (e: React.DragEvent<HTMLLIElement>) => {
    const zone = dropZoneTarget.current;
    const targetId = dragOverItem.current;

    // Se não tem zona fixa E não tem dragOverItem, ignora
    if (zone === "" && targetId === "") {
      return;
    }

    const item = components[dragItemComponent.current];
    const newItem = createNewItem(item.type);

    // Reset refs APÓS capturar os valores
    dragItemComponent.current = -1;
    dragOverItem.current = "";
    dropZoneTarget.current = "";

    if (!newItem) {
      return;
    }

    // ─── DROP EM ZONA FIXA (topo ou rodapé) ───────────────────────
    if (zone === "top" || zone === "bottom") {
      const cleanList = CheckoutFunctions.clearEmpty(list);
      let copyList = [...cleanList];

      if (zone === "top") {
        // Insere no início da lista raiz
        copyList.unshift(newItem);
      } else {
        // Insere no final da lista raiz
        copyList.push(newItem);
      }

      copyList = reorderList(copyList);
      setList(copyList);
      setEditItem(newItem);
      e.preventDefault();
      return;
    }

    // ─── DROP NORMAL (sobre um componente existente) ──────────────
    const cleanList = CheckoutFunctions.clearEmpty(list);
    const {
      parent,
      components: items,
      component: itemCurrent,
      index,
    } = CheckoutFunctions.getComponent(undefined, cleanList, targetId);

    if (items && itemCurrent) {
      // PROTEÇÃO: Nunca substituir ou dropar sobre o componente checkout
      if (itemCurrent.type === TCustomCheckoutComponentType.checkout) {
        return;
      }

      // Variáveis de trabalho
      const targetParent = parent;
      const targetItems = items;
      const targetComponent = itemCurrent;
      const targetIndex = index;

      // Se o alvo é um container (list), adiciona como FILHO.
      // Isso trata o caso da lateral do layout2_3: handleLateralDragEnter
      // seta dragOverItem para o lateral.id, que é do tipo list (container).
      if (isContainerItem(targetComponent)) {
        insertIntoContainer(targetComponent, newItem, cleanList);
      } else {
        // Caso normal: insere ao lado do componente alvo.
        // Para layout2_3: insere ANTES dele na lista (splice count 0).
        // Para none: SUBSTITUI o placeholder (splice count 1).
        let copyListItems = [...targetItems];

        copyListItems.splice(
          targetIndex,
          targetComponent.type === TCustomCheckoutComponentType.none ? 1 : 0,
          newItem,
        );

        copyListItems = CheckoutFunctions.clearEmpty(copyListItems);
        copyListItems = reorderList(copyListItems);

        if (!targetParent) {
          setList(copyListItems);
        } else {
          let newList = CheckoutFunctions.clearEmpty(cleanList);
          newList = newList.map((l) =>
            l.id === targetParent!.id
              ? { ...targetParent!, children: copyListItems }
              : {
                  ...l,
                  children: l.children.map((c) =>
                    c.id === targetParent!.id
                      ? { ...targetParent!, children: copyListItems }
                      : { ...c },
                  ),
                },
          );
          setList(newList);
        }
      }

      setEditItem(newItem);
      e.preventDefault();
    }
  };

  // ─── Handler de reordenação ──────────────────────────────────────

  /** Reordena um item existente via drag-and-drop */
  const dropItem = (
    e: React.DragEvent<HTMLDivElement>,
    dragItem: React.MutableRefObject<string>,
  ) => {
    const now = new Date();
    // Debounce de 300ms para evitar drops duplicados
    if (now.getTime() - dropItemDate.current.getTime() < 300) {
      return;
    }

    dropItemDate.current = now;
    const zone = dropZoneTarget.current;

    // Limpa empties antes de buscar posições
    const cleanList = CheckoutFunctions.clearEmpty(list);

    const {
      parent,
      components: items,
      component: itemCurrent,
      index: indexOld,
    } = CheckoutFunctions.getComponent(undefined, cleanList, dragItem.current);

    if (items && itemCurrent) {
      // PROTEÇÃO: Não permitir mover o componente checkout
      if (itemCurrent.type === TCustomCheckoutComponentType.checkout) {
        dragItem.current = "";
        dragOverItem.current = "";
        dropZoneTarget.current = "";
        return;
      }

      // ─── Reordenação para zonas fixas (topo/rodapé) ─────────────
      if (zone === "top" || zone === "bottom") {
        // Remove do local atual
        let copyItems = [...items];
        copyItems.splice(indexOld, 1);

        // Se estava em sub-lista (parent), atualiza a sub-lista
        let rootList: TCustomCheckoutComponentItem[];
        if (!parent) {
          rootList = copyItems;
        } else {
          rootList = cleanList.map((l) =>
            l.id === parent.id
              ? { ...parent, children: reorderList(copyItems) }
              : {
                  ...l,
                  children: l.children.map((c) =>
                    c.id === parent.id
                      ? { ...parent, children: reorderList(copyItems) }
                      : { ...c },
                  ),
                },
          );
        }

        // Insere na raiz
        if (zone === "top") {
          rootList.unshift(itemCurrent);
        } else {
          rootList.push(itemCurrent);
        }

        dragItem.current = "";
        dragOverItem.current = "";
        dropZoneTarget.current = "";
        setList(reorderList(rootList));
        e.preventDefault();
        return;
      }

      // ─── Reordenação normal ─────────────────────────────────────
      const { index } = CheckoutFunctions.getComponent(
        undefined,
        cleanList,
        dragOverItem.current,
      );

      let copyListItems = [...items];
      copyListItems.splice(indexOld, 1);

      // Ajusta index se necessário após remoção
      const adjustedIndex = index > indexOld ? index - 1 : index;
      copyListItems.splice(adjustedIndex, 0, itemCurrent);
      dragItem.current = "";
      dragOverItem.current = "";
      dropZoneTarget.current = "";

      copyListItems = reorderList(copyListItems);

      if (!parent) {
        setList(copyListItems);
      } else {
        const newList = cleanList.map((l) =>
          l.id === parent.id
            ? { ...parent, children: copyListItems }
            : {
                ...l,
                children: l.children.map((c) =>
                  c.id === parent.id ? { ...parent, children: copyListItems } : { ...c },
                ),
              },
        );
        setList(newList);
      }
      e.preventDefault();
    }
  };

  return {
    // Refs compartilhados (vão para o context)
    dragOverItem,
    dragOverItemOld,
    dropZoneTarget,
    // Handlers de paleta
    dragItemComponent,
    dragStartComponent,
    dragEnterComponent,
    dropComponent,
    // Handler de reordenação (vai para o context)
    dropItem,
  };
}
